import { useEffect, useMemo, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MessageSquare, RefreshCcw, AlertTriangle, Inbox as InboxIcon, User, Send, Reply, X, Globe, Sparkles, BookOpen, Star, Download, Bell, Heart, Building2, Check, ArrowRight, Volume2, VolumeX, Moon, Coffee, Play, Calendar as CalendarIcon, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { DamageFlagBadge } from "@/components/DamageExtras";
import { SyncedChip } from "@/lib/synced";
import { useSectionTheme } from "@/hooks/useSectionTheme";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const KINDS = [
  { key: null, label: "All" },
  { key: "email", label: "Emails" },
  { key: "sms", label: "SMS" },
  { key: "ota", label: "OTA" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-GB");
}

// Session Z30 — Chime tone library. Three tasteful presets for personal focus.
function playToneAt(ctx, tone, startAt) {
  if (tone === "ripple") {
    // Soft descending three-note plink: 880 → 1100 → 1320 Hz, staccato
    [880, 1100, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t0 = startAt + i * 0.12;
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, t0);
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.14, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.28);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(t0); osc.stop(t0 + 0.3);
    });
    return;
  }
  if (tone === "harp") {
    // Three-note chord (C5, E5, G5) triangle wave, single strum
    [523.25, 659.25, 783.99].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t0 = startAt + i * 0.05;
      osc.type = "triangle";
      osc.frequency.setValueAtTime(freq, t0);
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.11, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.55);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(t0); osc.stop(t0 + 0.6);
    });
    return;
  }
  // classic — original 880 → 1320 Hz sine sweep
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(880, startAt);
  osc.frequency.exponentialRampToValueAtTime(1320, startAt + 0.15);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(0.18, startAt + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.4);
  osc.connect(gain); gain.connect(ctx.destination);
  osc.start(startAt); osc.stop(startAt + 0.42);
}

const CHIME_TONES = [
  { key: "classic", label: "Classic", hint: "Bright sweep" },
  { key: "ripple", label: "Ripple", hint: "Three light plinks" },
  { key: "harp", label: "Harp", hint: "Gentle chord" },
];

export default function Inbox() {
  const sectionTheme = useSectionTheme("inbox");
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState({ items: [], unread: 0, total: 0 });
  const [kind, setKind] = useState(null);
  const [damageFlags, setDamageFlags] = useState({});
  useEffect(() => { api.get("/damage-flags").then((r) => setDamageFlags(r.data)).catch(() => {}); }, []);
  const [q, setQ] = useState("");
  const [waTemplates, setWaTemplates] = useState([]);
  const [waPromoteSuggestion, setWaPromoteSuggestion] = useState(null);
  const [confettiMuted, setConfettiMuted] = useState(false);
  const [confettiVariant, setConfettiVariant] = useState("burst");
  const [replyOpenId, setReplyOpenId] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const [replyOriginalDraft, setReplyOriginalDraft] = useState(""); // captured when AI draft is applied — used for Learn From Edits
  const [replySubject, setReplySubject] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [draftingId, setDraftingId] = useState(null);
  const [suggested, setSuggested] = useState(null);
  const [suggested2, setSuggested2] = useState(null);
  const [suggestionCtx, setSuggestionCtx] = useState("");
  const [draftTone, setDraftTone] = useState("warm");
  const [spend, setSpend] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [voice, setVoice] = useState(null);
  const [voiceExamples, setVoiceExamples] = useState(null); // { examples, total } once loaded
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceCategoryFilter, setVoiceCategoryFilter] = useState(null); // null = all
  const [detectedCategory, setDetectedCategory] = useState(null); // populated by SSE during draft
  const [detectedConfidence, setDetectedConfidence] = useState(null);
  const [effectiveCategory, setEffectiveCategory] = useState(null); // pool actually used
  const [overrideUsed, setOverrideUsed] = useState(false);
  const [categoryMenuFor, setCategoryMenuFor] = useState(null); // message id with open dropdown
  const [inboxSyncedAt, setInboxSyncedAt] = useState(null);
  const [inboxRefreshing, setInboxRefreshing] = useState(false);
  const [chimeMuted, setChimeMuted] = useState(false); // Session Z27 — synced from /auth/me
  const chimeMutedRef = useRef(false);
  useEffect(() => { chimeMutedRef.current = chimeMuted; }, [chimeMuted]);
  const [quietHours, setQuietHours] = useState({ enabled: false, start: "22:00", end: "07:00" });
  const quietHoursRef = useRef(quietHours);
  useEffect(() => { quietHoursRef.current = quietHours; }, [quietHours]);
  const isInQuietHoursRef = useRef(() => false);
  const [quietMenuOpen, setQuietMenuOpen] = useState(false);
  // Session Z29 — Snooze Once (client-side 2-hour temporary chime silence)
  const [snoozeUntil, setSnoozeUntil] = useState(() => {
    const raw = parseInt(localStorage.getItem("ns_snooze_until") || "0", 10) || 0;
    return raw > Date.now() ? raw : 0;
  });
  const snoozeUntilRef = useRef(snoozeUntil);
  useEffect(() => { snoozeUntilRef.current = snoozeUntil; }, [snoozeUntil]);
  const [snoozeTick, setSnoozeTick] = useState(0);
  useEffect(() => {
    if (!snoozeUntil) return undefined;
    const t = setInterval(() => {
      setSnoozeTick((n) => n + 1);
      if (Date.now() >= snoozeUntilRef.current) {
        setSnoozeUntil(0);
        localStorage.removeItem("ns_snooze_until");
        localStorage.removeItem("ns_focus_mode"); // Z33 — clear overlay when snooze expires naturally
        window.dispatchEvent(new CustomEvent("ns:focus-mode"));
      }
    }, 30000);
    return () => clearInterval(t);
  }, [snoozeUntil]);
  const [snoozeMenuOpen, setSnoozeMenuOpen] = useState(false);
  const [chimeTone, setChimeTone] = useState("classic");
  const chimeToneRef = useRef("classic");
  useEffect(() => { chimeToneRef.current = chimeTone; }, [chimeTone]);
  // Session Z31 — Google Calendar snooze suggestion
  const [gcalStatus, setGcalStatus] = useState({ configured: false, connected: false });
  // Session Z79 — Outlook / Microsoft Graph snooze suggestion (mirrors gcal)
  const [outlookStatus, setOutlookStatus] = useState({ configured: false, connected: false });
  const [currentMeeting, setCurrentMeeting] = useState(null);
  const [autoSnoozeCount, setAutoSnoozeCount] = useState(0);
  const loadGcalStatus = () => {
    api.get("/oauth/calendar/status")
      .then((r) => setGcalStatus(r.data || {}))
      .catch(() => {});
  };
  const loadOutlookStatus = () => {
    api.get("/oauth/outlook/status")
      .then((r) => setOutlookStatus(r.data || {}))
      .catch(() => {});
  };
  const loadCurrentMeeting = () => {
    api.get("/calendar/current-meeting")
      .then((r) => {
        const ev = r.data?.event || null;
        setCurrentMeeting(ev);
        // Session Z32 — Meeting-Auto Snooze: silence the chime the instant a
        // real meeting is detected. Dedupe by event id so refresh doesn't
        // re-trigger; localStorage tracks which events we've handled here.
        if (ev && ev.id) {
          const handled = JSON.parse(localStorage.getItem("ns_auto_snoozed_ids") || "[]");
          const alreadyActive = snoozeUntilRef.current && Date.now() < snoozeUntilRef.current;
          if (!handled.includes(ev.id) && !alreadyActive) {
            const until = Date.now() + ev.minutes_left * 60 * 1000;
            localStorage.setItem("ns_snooze_until", String(until));
            setSnoozeUntil(until);
            // Session Z33 — flip on Focus Mode Overlay app-wide.
            localStorage.setItem("ns_focus_mode", JSON.stringify({
              summary: ev.summary,
              end_iso: ev.end_iso,
              event_id: ev.id,
            }));
            window.dispatchEvent(new CustomEvent("ns:focus-mode"));
            const nextHandled = [...handled, ev.id].slice(-100);
            localStorage.setItem("ns_auto_snoozed_ids", JSON.stringify(nextHandled));
            toast.info(`Meeting detected — chime silenced until ${ev.end_pretty}`);
            api.post("/calendar/auto-snoozed", {
              event_id: ev.id,
              event_summary: ev.summary,
              end_iso: ev.end_iso,
              minutes: ev.minutes_left,
            }).then((r) => setAutoSnoozeCount(r.data?.count || 0))
              .catch(() => {});
          }
        }
      })
      .catch(() => setCurrentMeeting(null));
  };
  const connectGcal = async () => {
    try {
      const r = await api.get("/oauth/calendar/login");
      const w = window.open(r.data.authorization_url, "gcal_oauth", "width=560,height=680");
      if (!w) toast.error("Popup blocked — allow pop-ups for this site");
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const disconnectGcal = async () => {
    try {
      await api.delete("/oauth/calendar/disconnect");
      setGcalStatus((s) => ({ ...s, connected: false, connected_email: null }));
      setCurrentMeeting(null);
      toast.success("Google Calendar disconnected");
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const connectOutlook = async () => {
    try {
      const r = await api.get("/oauth/outlook/login");
      const w = window.open(r.data.authorization_url, "outlook_oauth", "width=560,height=680");
      if (!w) toast.error("Popup blocked — allow pop-ups for this site");
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const disconnectOutlook = async () => {
    try {
      await api.delete("/oauth/outlook/disconnect");
      setOutlookStatus((s) => ({ ...s, connected: false, connected_email: null }));
      setCurrentMeeting(null);
      toast.success("Outlook Calendar disconnected");
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  useEffect(() => {
    loadGcalStatus();
    loadOutlookStatus();
    loadCurrentMeeting();
    const t = setInterval(loadCurrentMeeting, 5 * 60 * 1000); // every 5 min
    const onMsg = (e) => {
      if (e?.data?.type === "gcal_connected" && e.data.ok) {
        loadGcalStatus();
        loadCurrentMeeting();
        toast.success("Google Calendar connected");
      }
      if (e?.data?.type === "outlook_connected" && e.data.ok) {
        loadOutlookStatus();
        loadCurrentMeeting();
        toast.success("Outlook Calendar connected");
      }
    };
    window.addEventListener("message", onMsg);
    return () => { clearInterval(t); window.removeEventListener("message", onMsg); };
  }, []);
  const activateSnooze = (mins = 120, label = "2 hours") => {
    const until = Date.now() + mins * 60 * 1000;
    localStorage.setItem("ns_snooze_until", String(until));
    setSnoozeUntil(until);
    setSnoozeMenuOpen(false);
    toast.success(`Chime snoozed for ${label}`);
  };
  const persistChimeTone = async (tone) => {
    setChimeTone(tone);
    try {
      await api.patch("/auth/profile", { chime_tone: tone });
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const previewChime = (tone) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      playToneAt(ctx, tone, ctx.currentTime);
    } catch { /* ignore */ }
  };
  const cancelSnooze = () => {
    localStorage.removeItem("ns_snooze_until");
    localStorage.removeItem("ns_focus_mode"); // Z33 — clear overlay on manual cancel
    window.dispatchEvent(new CustomEvent("ns:focus-mode"));
    setSnoozeUntil(0);
    toast.success("Snooze cancelled");
  };
  const snoozeRemaining = () => {
    if (!snoozeUntil) return "";
    const ms = snoozeUntil - Date.now();
    if (ms <= 0) return "";
    const mins = Math.ceil(ms / 60000);
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  };

  const isInQuietHours = () => {
    const q = quietHoursRef.current;
    if (!q?.enabled) return false;
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const [sh, sm] = (q.start || "22:00").split(":").map((n) => parseInt(n, 10) || 0);
    const [eh, em] = (q.end || "07:00").split(":").map((n) => parseInt(n, 10) || 0);
    const startMin = sh * 60 + sm;
    const endMin = eh * 60 + em;
    if (startMin === endMin) return false;
    // Wrap-around (e.g. 22:00 → 07:00)
    return startMin < endMin
      ? nowMin >= startMin && nowMin < endMin
      : nowMin >= startMin || nowMin < endMin;
  };
  useEffect(() => { isInQuietHoursRef.current = isInQuietHours; });

  const toggleChimeMute = async () => {
    const next = !chimeMuted;
    setChimeMuted(next); // optimistic
    try {
      await api.patch("/auth/profile", { chime_muted: next });
      toast.success(next ? "Chime muted — toasts still on" : "Chime back on");
    } catch (err) {
      setChimeMuted(!next);
      toast.error(apiError(err));
    }
  };

  const persistQuietHours = async (next) => {
    setQuietHours(next); // optimistic
    try {
      await api.patch("/auth/profile", { quiet_hours: next });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const load = () => {
    setLoading(true);
    setInboxRefreshing(true);
    api
      .get("/inbox", { params: kind ? { kind } : {} })
      .then((r) => { setData(r.data); setInboxSyncedAt(new Date()); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => { setLoading(false); setInboxRefreshing(false); });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);
  useEffect(() => {
    // Session Z41 — hydrate WhatsApp template chips for the composer.
    api.get("/inbox/whatsapp-templates")
      .then((r) => {
        setWaTemplates(r.data?.templates || []);
        setWaPromoteSuggestion(r.data?.promote_suggestion || null);
      })
      .catch(() => {});
    // Session Z46 — honour the "mute celebrations" workspace toggle.
    api.get("/settings/ui-prefs")
      .then((r) => {
        setConfettiMuted(!!r.data?.confetti_muted);
        setConfettiVariant(r.data?.confetti_variant || "burst");
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/inbox/draft-spend").then((r) => setSpend(r.data)).catch(() => {});
    api.get("/inbox/draft-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/inbox/voice-metrics").then((r) => setVoice(r.data)).catch(() => {});
    // Session Z26 — real-time demand alerts. Poll every 30 s while the tab
    // is visible; a subtle chime + toast fires on any unread bump.
    let lastUnread = 0;
    let firstLoad = true;
    let audioCtx = null;
    // Session Z27 — pull the user's synced chime preference on mount. Toasts
    // still fire on every unread bump; the chime is what mutes.
    api.get("/auth/me").then((r) => {
      setChimeMuted(Boolean(r.data?.chime_muted));
      if (r.data?.quiet_hours) setQuietHours(r.data.quiet_hours);
      if (r.data?.chime_tone) setChimeTone(r.data.chime_tone);
      if (typeof r.data?.meeting_auto_snooze_count === "number") {
        setAutoSnoozeCount(r.data.meeting_auto_snooze_count);
      }
    }).catch(() => {});
    const chime = () => {
      if (chimeMutedRef.current) return;
      if (isInQuietHoursRef.current && isInQuietHoursRef.current()) return;
      if (snoozeUntilRef.current && Date.now() < snoozeUntilRef.current) return;
      try {
        audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
        playToneAt(audioCtx, chimeToneRef.current, audioCtx.currentTime);
      } catch { /* ignore */ }
    };
    const poll = () => {
      if (document.hidden) return;
      api.get("/notifications?limit=10").then((r) => {
        setNotifications(r.data);
        if (!firstLoad && r.data.unread > lastUnread) {
          const delta = r.data.unread - lastUnread;
          const newest = r.data.items.find((n) => !n.read);
          toast(
            <span data-testid="inbox-demand-live-toast">
              <strong>{delta} new demand signal{delta === 1 ? "" : "s"}</strong>
              {newest?.property_name ? ` · ${newest.property_name}` : ""}
            </span>,
            { icon: <Bell className="w-4 h-4 text-amber-600" /> },
          );
          chime();
        }
        lastUnread = r.data.unread;
        firstLoad = false;
      }).catch(() => {});
    };
    poll();
    const timer = setInterval(poll, 30000);
    const onVis = () => { if (!document.hidden) poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const markNotificationRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) => ({
        ...prev,
        items: prev.items.map((n) => (n.id === id ? { ...n, read: true } : n)),
        unread: Math.max(0, prev.unread - 1),
      }));
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications((prev) => ({
        ...prev,
        items: prev.items.map((n) => ({ ...n, read: true })),
        unread: 0,
      }));
      toast.success("All caught up");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const applyTemplate = (t) => {
    setReplyBody(t.body);
    setReplyOriginalDraft("");
    api.post(`/inbox/draft-templates/${t.id}/use`).catch(() => {});
    setTemplates((prev) => prev.map((x) =>
      x.id === t.id ? { ...x, usage_count: (x.usage_count || 0) + 1 } : x
    ).sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0)));
  };

  const saveTemplate = async () => {
    const title = (window.prompt("Template title", replyBody.slice(0, 40)) || "").trim();
    if (!title || !replyBody.trim()) return;
    try {
      const { data } = await api.post("/inbox/draft-templates", {
        title, body: replyBody, kind: "email",
      });
      setTemplates((prev) => [data, ...prev]);
      toast.success("Template saved");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/inbox/draft-templates/${id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!q) return data.messages;
    const s = q.toLowerCase();
    return data.messages.filter((m) =>
      (m.contact_name || "").toLowerCase().includes(s)
      || (m.to || "").toLowerCase().includes(s)
      || (m.subject || "").toLowerCase().includes(s)
      || (m.preview || "").toLowerCase().includes(s)
      || (m.property_name || "").toLowerCase().includes(s)
    );
  }, [data, q]);

  const counts = data?.counts || { email: 0, sms: 0, ota: 0, failed: 0 };

  const openReply = (m) => {
    setReplyOpenId(m.id);
    setReplyBody("");
    setReplyOriginalDraft("");
    setReplySubject(m.kind === "email" ? `Re: ${m.subject || "(no subject)"}` : "");
    const text = [m.subject, m.preview].filter(Boolean).join(" ");
    setSuggestionCtx(text);
    if (text) {
      api.post("/inbox/suggest-template", { incoming_text: text, limit: 2 })
        .then((r) => {
          setSuggested(r.data.suggestions?.[0] || null);
          setSuggested2(r.data.suggestions?.[1] || null);
        })
        .catch(() => { setSuggested(null); setSuggested2(null); });
    } else {
      setSuggested(null); setSuggested2(null);
    }
  };

  const flagNotAMatch = async (tpl) => {
    try {
      await api.post("/inbox/suggest-feedback", {
        template_id: tpl.id,
        incoming_text: suggestionCtx,
        signal: "not_a_match",
      });
      if (suggested?.id === tpl.id) setSuggested(suggested2);
      if (suggested2?.id === tpl.id) setSuggested2(null);
      toast.success("Thanks — that template will be demoted next time");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const sendReply = async (m) => {
    if (!replyBody.trim()) { toast.error("Reply body is empty"); return; }
    setReplyBusy(true);
    try {
      const isWhatsApp = m.channel === "whatsapp";
      const target = m.kind === "ota" ? m.from : m.to;
      const kind = isWhatsApp ? "whatsapp" : (m.kind === "sms" ? "sms" : "email");
      const { data: resp } = await api.post("/inbox/reply", {
        kind,
        to: target,
        subject: replySubject,
        body: replyBody,
        contact_id: m.contact_id,
      });
      if (resp.ok) {
        // Learn From Edits: if the user tweaked an AI draft before sending, log the pair
        if (replyOriginalDraft && replyOriginalDraft.trim() !== replyBody.trim()) {
          api.post("/inbox/draft-feedback", {
            ai_draft: replyOriginalDraft,
            edited_final: replyBody,
            kind: isWhatsApp ? "whatsapp" : (m.kind === "sms" ? "sms" : (m.kind || "email")),
            tone: draftTone,
          }).catch(() => {});
        }
        toast.success(
          isWhatsApp ? "WhatsApp reply sent" :
          m.kind === "sms" ? "SMS sent" : "Reply sent",
        );
        setReplyOpenId(null);
        setReplyBody("");
        setReplyOriginalDraft("");
        load();
      } else {
        toast.error("Send failed — provider returned an error");
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setReplyBusy(false);
    }
  };

  const draftReply = async (m, overrideCategory = null) => {
    setDraftingId(m.id);
    if (!overrideCategory) {
      setDetectedCategory(null);
      setDetectedConfidence(null);
      setEffectiveCategory(null);
      setOverrideUsed(false);
    }
    // Streaming path: consume SSE tokens as they arrive
    try {
      if (replyOpenId !== m.id) openReply(m);
      setReplyBody("");
      setReplyOriginalDraft("");
      const token = localStorage.getItem("ns_token") || "";
      const resp = await fetch(`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/inbox/draft-reply/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          original_kind: m.kind,
          original_channel: m.channel,
          original_subject: m.subject,
          original_body: m.preview || m.subject,
          contact_id: m.contact_id,
          contact_name: m.contact_name,
          property_name: m.property_name,
          tone: draftTone,
          override_category: overrideCategory || undefined,
        }),
      });
      if (!resp.ok) {
        const errText = await resp.text().catch(() => "");
        let msg = `Draft failed (${resp.status})`;
        try {
          const j = JSON.parse(errText);
          msg = j.detail || msg;
        } catch { /* text */ }
        toast.error(msg);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      let done = false;
      while (!done) {
        const { value, done: streamDone } = await reader.read();
        if (streamDone) break;
        buffer += decoder.decode(value, { stream: true });
        // Parse SSE events (event:...\ndata:...\n\n)
        let idx;
        while ((idx = buffer.indexOf("\n\n")) >= 0) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          let evtName = "message";
          let dataStr = "";
          for (const line of frame.split("\n")) {
            if (line.startsWith("event: ")) evtName = line.slice(7).trim();
            else if (line.startsWith("data: ")) dataStr += line.slice(6);
          }
          if (!dataStr) continue;
          try {
            const payload = JSON.parse(dataStr);
            if (evtName === "token" && payload.delta) {
              acc += payload.delta;
              setReplyBody(acc);
            } else if (evtName === "category" && (payload.category || payload.effective_category)) {
              if (payload.category) setDetectedCategory(payload.category);
              if (typeof payload.confidence === "number") setDetectedConfidence(payload.confidence);
              if (payload.effective_category !== undefined) setEffectiveCategory(payload.effective_category);
              if (payload.override_used !== undefined) setOverrideUsed(!!payload.override_used);
            } else if (evtName === "done") {
              done = true;
              setReplyOriginalDraft(acc);
              if (payload.effective_category !== undefined) setEffectiveCategory(payload.effective_category);
              if (payload.override_used !== undefined) setOverrideUsed(!!payload.override_used);
              const cost = payload.cost_usd_est ? ` · $${payload.cost_usd_est.toFixed(5)}` : "";
              toast.success(`${draftTone} draft ready${cost} — tweak and send`);
              try {
                const { data: s } = await api.get("/inbox/draft-spend");
                setSpend(s);
              } catch { /* soft */ }
            } else if (evtName === "error") {
              done = true;
              toast.error(payload.message || "AI stream failed");
            }
          } catch { /* skip malformed frame */ }
        }
      }
    } catch (err) {
      toast.error(err?.message || "Draft stream failed");
    } finally {
      setDraftingId(null);
    }
  };

  return (
    <Layout
      title="Inbox"
      subtitle="Every message we've sent — emails, SMS, OTA threads and journey nudges — in one stream."
      actions={
        <>
          <Input
            data-testid="inbox-search"
            placeholder="Guest, subject, property…"
            className="h-11 w-64 bg-white"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button variant="outline" onClick={load} className="rounded-full h-11 px-5">
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </>
      }
    >
      <div data-testid="inbox-root" className="space-y-6">
        {notifications.items.length > 0 && (
          <div
            data-testid="inbox-demand-alerts"
            className="ns-card p-5 border-l-4 border-amber-400 bg-amber-50/40"
          >
            <div className="flex items-center gap-3 mb-3">
              <Bell className="w-4 h-4 text-amber-600" />
              <div className="flex-1 min-w-0">
                <div className="ns-label text-amber-800">
                  Demand signals
                  {notifications.unread > 0 && (
                    <span
                      data-testid="inbox-demand-unread-badge"
                      className="ml-2 inline-flex items-center rounded-full bg-amber-600 text-white text-[10px] font-medium px-2 py-0.5"
                    >
                      {notifications.unread} new
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Guests who tried to book a fully-taken window and joined the wishlist.
                </div>
              </div>
              {notifications.unread > 0 && (
                <button
                  data-testid="inbox-demand-mark-all-read"
                  onClick={markAllRead}
                  className="text-[11px] rounded-full bg-white text-slate-600 border border-slate-200 px-3 py-1 hover:bg-stone-50"
                >
                  Mark all read
                </button>
              )}
              <button
                type="button"
                data-testid="inbox-chime-mute-toggle"
                onClick={toggleChimeMute}
                title={chimeMuted ? "Chime is muted — click to enable" : "Mute chime (toasts stay on)"}
                className={`inline-flex items-center justify-center h-7 w-7 rounded-full border transition ${
                  chimeMuted
                    ? "bg-slate-100 border-slate-200 text-slate-500 hover:bg-slate-200"
                    : "bg-white border-amber-200 text-amber-700 hover:bg-amber-50"
                }`}
              >
                {chimeMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
              </button>
              <div className="relative">
                <button
                  type="button"
                  data-testid="inbox-quiet-hours-toggle"
                  onClick={() => setQuietMenuOpen((v) => !v)}
                  title={
                    quietHours.enabled
                      ? `Do Not Disturb: ${quietHours.start} → ${quietHours.end}`
                      : "Do Not Disturb (nightly quiet hours)"
                  }
                  className={`inline-flex items-center justify-center h-7 w-7 rounded-full border transition ${
                    quietHours.enabled
                      ? "bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                      : "bg-white border-slate-200 text-slate-500 hover:bg-stone-50"
                  }`}
                >
                  <Moon className="w-3.5 h-3.5" />
                </button>
                {quietMenuOpen && (
                  <div
                    data-testid="inbox-quiet-hours-menu"
                    className="absolute right-0 top-9 z-30 w-64 rounded-xl bg-white border border-slate-200 shadow-lg p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="ns-label text-[10px] text-slate-500">Do Not Disturb</div>
                        <div className="text-[11px] text-slate-400">Silence the chime overnight</div>
                      </div>
                      <button
                        type="button"
                        data-testid="inbox-quiet-hours-enable"
                        onClick={() => persistQuietHours({ ...quietHours, enabled: !quietHours.enabled })}
                        className={`relative w-9 h-5 rounded-full transition ${
                          quietHours.enabled ? "bg-indigo-600" : "bg-slate-200"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition ${
                            quietHours.enabled ? "left-4" : "left-0.5"
                          }`}
                        />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <label className="block">
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Start</div>
                        <input
                          type="time"
                          data-testid="inbox-quiet-hours-start"
                          value={quietHours.start}
                          onChange={(e) =>
                            persistQuietHours({ ...quietHours, start: e.target.value })
                          }
                          disabled={!quietHours.enabled}
                          className="w-full h-8 rounded-lg border border-slate-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400 disabled:opacity-60"
                        />
                      </label>
                      <label className="block">
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">End</div>
                        <input
                          type="time"
                          data-testid="inbox-quiet-hours-end"
                          value={quietHours.end}
                          onChange={(e) =>
                            persistQuietHours({ ...quietHours, end: e.target.value })
                          }
                          disabled={!quietHours.enabled}
                          className="w-full h-8 rounded-lg border border-slate-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400 disabled:opacity-60"
                        />
                      </label>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-2 leading-relaxed">
                      Toasts still appear silently. The chime resumes automatically at end time.
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <div className="ns-label text-[10px] text-slate-500 mb-1.5">Chime tone</div>
                      <div className="space-y-1" data-testid="inbox-chime-tone-picker">
                        {CHIME_TONES.map((t) => (
                          <div key={t.key} className="flex items-center gap-2">
                            <button
                              type="button"
                              data-testid={`inbox-chime-tone-${t.key}`}
                              onClick={() => persistChimeTone(t.key)}
                              className={`flex-1 flex items-center gap-2 text-left rounded-lg px-2 py-1.5 border transition ${
                                chimeTone === t.key
                                  ? "border-teal-300 bg-teal-50/60"
                                  : "border-transparent hover:bg-stone-50"
                              }`}
                            >
                              <span
                                className={`w-3 h-3 rounded-full border ${
                                  chimeTone === t.key ? "bg-teal-600 border-teal-600" : "bg-white border-slate-300"
                                }`}
                              />
                              <div className="min-w-0">
                                <div className="text-xs font-medium">{t.label}</div>
                                <div className="text-[10px] text-slate-400">{t.hint}</div>
                              </div>
                            </button>
                            <button
                              type="button"
                              data-testid={`inbox-chime-tone-preview-${t.key}`}
                              onClick={() => previewChime(t.key)}
                              title="Preview"
                              className="text-slate-300 hover:text-teal-700 transition"
                            >
                              <Play className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {snoozeUntil ? (
                <button
                  type="button"
                  data-testid="inbox-snooze-cancel"
                  onClick={cancelSnooze}
                  title="Chime resumes when clicked"
                  className="inline-flex items-center gap-1 h-7 rounded-full border border-amber-300 bg-amber-50 text-amber-800 px-2.5 text-[10px] font-medium hover:bg-amber-100 transition"
                >
                  <Coffee className="w-3 h-3" />
                  <span data-testid="inbox-snooze-remaining">{snoozeRemaining()}</span>
                  {autoSnoozeCount > 0 && (
                    <span
                      data-testid="inbox-auto-snooze-badge-active"
                      className="ml-1 text-[9px] text-indigo-700 bg-indigo-50 rounded-full px-1 py-0.5 border border-indigo-200"
                      title={`${autoSnoozeCount} meetings auto-silenced`}
                    >
                      Auto ×{autoSnoozeCount}
                    </span>
                  )}
                </button>
              ) : (
                <div className="relative">
                  <button
                    type="button"
                    data-testid="inbox-snooze-start"
                    onClick={() => setSnoozeMenuOpen((v) => !v)}
                    title="Silence the chime temporarily"
                    className="inline-flex items-center gap-1 h-7 rounded-full bg-white border border-slate-200 text-slate-500 hover:bg-stone-50 hover:text-amber-700 transition px-2"
                  >
                    <Coffee className="w-3.5 h-3.5" />
                    {autoSnoozeCount > 0 && (
                      <span
                        data-testid="inbox-auto-snooze-badge"
                        className="text-[9px] text-indigo-700 bg-indigo-50 rounded-full px-1.5 py-0.5 border border-indigo-200"
                        title={`Meetings have auto-silenced the chime ${autoSnoozeCount} time${autoSnoozeCount === 1 ? "" : "s"}`}
                      >
                        Auto ×{autoSnoozeCount}
                      </span>
                    )}
                  </button>
                  {snoozeMenuOpen && (
                    <div
                      data-testid="inbox-snooze-menu"
                      className="absolute right-0 top-9 z-30 w-64 rounded-xl bg-white border border-slate-200 shadow-lg p-2"
                    >
                      <div className="ns-label text-[10px] text-slate-500 px-2 pt-1 pb-1.5">Snooze chime</div>
                      {currentMeeting && (
                        <>
                          <button
                            type="button"
                            data-testid="inbox-snooze-meeting-suggest"
                            onClick={() => activateSnooze(
                              currentMeeting.minutes_left,
                              `end of "${currentMeeting.summary}"`,
                            )}
                            className="w-full text-left text-xs rounded-lg bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-2 py-2 mb-1"
                          >
                            <div className="flex items-center gap-1.5 text-indigo-900 font-medium">
                              <CalendarIcon className="w-3 h-3" />
                              Until end of meeting
                            </div>
                            <div className="text-[10px] text-indigo-700/80 truncate mt-0.5">
                              {currentMeeting.summary} · {currentMeeting.end_pretty}
                            </div>
                            {currentMeeting.source && currentMeeting.source !== "primary" && (
                              <div className="text-[10px] text-indigo-500 truncate mt-0.5">
                                via {currentMeeting.source}
                              </div>
                            )}
                          </button>
                          {currentMeeting.join_url && (
                            <a
                              data-testid="inbox-snooze-join-meeting"
                              href={currentMeeting.join_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => setSnoozeMenuOpen(false)}
                              className="w-full flex items-center gap-1.5 text-xs rounded-lg bg-sky-600 hover:bg-sky-700 text-white px-2 py-2 mb-1"
                            >
                              <LinkIcon className="w-3 h-3" />
                              <span className="font-medium">
                                Join {currentMeeting.provider === "outlook" ? "Teams" : "meeting"}
                              </span>
                            </a>
                          )}
                        </>
                      )}
                      <button
                        type="button"
                        data-testid="inbox-snooze-30m"
                        onClick={() => activateSnooze(30, "30 minutes")}
                        className="w-full text-left text-xs rounded-lg hover:bg-stone-50 px-2 py-2"
                      >
                        <div className="font-medium">30 minutes</div>
                        <div className="text-[10px] text-slate-400">Quick focus sprint</div>
                      </button>
                      <button
                        type="button"
                        data-testid="inbox-snooze-60m"
                        onClick={() => activateSnooze(60, "1 hour · meeting")}
                        className="w-full text-left text-xs rounded-lg hover:bg-stone-50 px-2 py-2"
                      >
                        <div className="font-medium">Until end of meeting</div>
                        <div className="text-[10px] text-slate-400">1 hour</div>
                      </button>
                      <button
                        type="button"
                        data-testid="inbox-snooze-2h"
                        onClick={() => activateSnooze(120, "2 hours")}
                        className="w-full text-left text-xs rounded-lg hover:bg-stone-50 px-2 py-2"
                      >
                        <div className="font-medium">2 hours</div>
                        <div className="text-[10px] text-slate-400">Deep-work block</div>
                      </button>
                      {gcalStatus.configured && (
                        <div className="mt-1 pt-1.5 border-t border-slate-100">
                          {gcalStatus.connected ? (
                            <button
                              type="button"
                              data-testid="inbox-gcal-disconnect"
                              onClick={disconnectGcal}
                              className="w-full text-left text-[10px] rounded-lg hover:bg-stone-50 px-2 py-1.5 text-slate-500"
                              title={gcalStatus.connected_email || ""}
                            >
                              <span className="inline-flex items-center gap-1.5">
                                <CalendarIcon className="w-3 h-3 text-emerald-600" />
                                Google Calendar connected
                              </span>
                              <div className="text-[10px] text-slate-400 truncate">
                                {gcalStatus.connected_email} · click to disconnect
                              </div>
                            </button>
                          ) : (
                            <button
                              type="button"
                              data-testid="inbox-gcal-connect"
                              onClick={connectGcal}
                              className="w-full text-left text-[11px] rounded-lg hover:bg-indigo-50 px-2 py-1.5 text-indigo-700 font-medium"
                            >
                              <span className="inline-flex items-center gap-1.5">
                                <CalendarIcon className="w-3 h-3" />
                                Connect Google Calendar
                              </span>
                              <div className="text-[10px] text-slate-400">Auto-suggest &quot;until meeting ends&quot;</div>
                            </button>
                          )}
                        </div>
                      )}
                      {outlookStatus.configured && (
                        <div className="mt-1 pt-1.5 border-t border-slate-100">
                          {outlookStatus.connected ? (
                            <button
                              type="button"
                              data-testid="inbox-outlook-disconnect"
                              onClick={disconnectOutlook}
                              className="w-full text-left text-[10px] rounded-lg hover:bg-stone-50 px-2 py-1.5 text-slate-500"
                              title={outlookStatus.connected_email || ""}
                            >
                              <span className="inline-flex items-center gap-1.5">
                                <CalendarIcon className="w-3 h-3 text-sky-600" />
                                Outlook Calendar connected
                              </span>
                              <div className="text-[10px] text-slate-400 truncate">
                                {outlookStatus.connected_email} · click to disconnect
                              </div>
                            </button>
                          ) : (
                            <button
                              type="button"
                              data-testid="inbox-outlook-connect"
                              onClick={connectOutlook}
                              className="w-full text-left text-[11px] rounded-lg hover:bg-sky-50 px-2 py-1.5 text-sky-700 font-medium"
                            >
                              <span className="inline-flex items-center gap-1.5">
                                <CalendarIcon className="w-3 h-3" />
                                Connect Outlook Calendar
                              </span>
                              <div className="text-[10px] text-slate-400">Same auto-snooze for Teams meetings</div>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              {notifications.items.slice(0, 5).map((n) => {
                const dateParam = n.check_in ? `&date=${n.check_in}` : "";
                const link = `/calendar?highlight=${n.property_id}${dateParam}`;
                const guestLabel = n.guest_first_name
                  ? `${n.guest_first_name} <${n.guest_email}>`
                  : n.guest_email;
                return (
                  <div
                    key={n.id}
                    data-testid={`inbox-demand-item-${n.id}`}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 text-xs ${
                      n.read ? "bg-white/60 opacity-60" : "bg-white ring-1 ring-amber-100"
                    }`}
                  >
                    <Building2 className="w-3.5 h-3.5 text-teal-700 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{n.property_name || "Unknown unit"}</div>
                      <div className="text-slate-500 truncate mt-0.5">
                        {guestLabel}
                        {n.check_in && n.check_out && (
                          <span className="text-slate-400"> · {n.check_in} → {n.check_out}</span>
                        )}
                      </div>
                    </div>
                    <a
                      href={link}
                      onClick={() => !n.read && markNotificationRead(n.id)}
                      data-testid={`inbox-demand-open-${n.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-teal-700 text-white px-2.5 py-1 hover:bg-teal-800 transition"
                    >
                      Open <ArrowRight className="w-3 h-3" />
                    </a>
                    {!n.read && (
                      <button
                        data-testid={`inbox-demand-dismiss-${n.id}`}
                        onClick={() => markNotificationRead(n.id)}
                        className="text-slate-400 hover:text-slate-700"
                        title="Mark as read"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <section className={`ns-section ns-section-${sectionTheme}`} data-section-theme={sectionTheme}>
          <div className="flex items-start gap-3 mb-4">
            <div className={`ns-rail ns-rail-${sectionTheme} self-stretch min-h-[2.5rem]`} />
            <div className="flex-1 min-w-0">
              <div className="ns-label text-indigo-800">Message volume</div>
              <div className="font-serif text-xl text-slate-900 mt-0.5">Every channel at a glance</div>
            </div>
            <SyncedChip syncedAt={inboxSyncedAt} busy={inboxRefreshing} testid="inbox-synced-chip" onRefresh={() => load()} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: "All messages", value: data?.total ?? 0, icon: <InboxIcon className="w-4 h-4" />, tone: "navy" },
              { label: "Emails", value: counts.email, icon: <Mail className="w-4 h-4" />, tone: "teal" },
              { label: "SMS", value: counts.sms, icon: <MessageSquare className="w-4 h-4" />, tone: "amber" },
              { label: "OTA", value: counts.ota, icon: <Globe className="w-4 h-4" />, tone: "indigo" },
              { label: "Failed", value: counts.failed, icon: <AlertTriangle className="w-4 h-4" />, tone: "red" },
            ].map((s) => {
              const toneBg = s.tone === "teal" ? "bg-teal-50 text-teal-700 border-teal-100"
                : s.tone === "amber" ? "bg-amber-50 text-amber-700 border-amber-100"
                : s.tone === "indigo" ? "bg-indigo-50 text-indigo-700 border-indigo-100"
                : s.tone === "red" ? "bg-red-50 text-red-700 border-red-100"
                : "bg-slate-50 text-slate-700 border-slate-200";
              return (
                <div key={s.label} className="ns-card p-5 relative overflow-hidden">
                  <div className={`absolute inset-x-0 top-0 h-1 ${s.tone === "teal" ? "bg-teal-500" : s.tone === "amber" ? "bg-amber-500" : s.tone === "indigo" ? "bg-indigo-500" : s.tone === "red" ? "bg-red-500" : "bg-slate-400"}`} />
                  <div className={`ns-label inline-flex items-center gap-2 rounded-full border px-2.5 py-1 ${toneBg}`}>
                    {s.icon}{s.label}
                  </div>
                  <div className="font-serif text-3xl mt-3 text-slate-900">{s.value}</div>
                </div>
              );
            })}
          </div>
        </section>

        {spend && spend.count_all > 0 && (
          <div data-testid="inbox-ai-spend" className="ns-card p-4 border-l-4 border-indigo-500 bg-indigo-50/30 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <div className="ns-label text-indigo-800">AI drafts spend</div>
                <div className="text-sm text-slate-700 mt-0.5">
                  <span className="font-mono">${spend.cost_usd_month.toFixed(4)}</span> this month
                  <span className="text-slate-400"> · </span>
                  <span className="font-mono">${spend.cost_usd_week.toFixed(4)}</span> this week
                  <span className="text-slate-400"> · </span>
                  <span className="font-mono">${spend.cost_usd_all.toFixed(4)}</span> lifetime
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {spend.count_all} draft{spend.count_all === 1 ? "" : "s"} generated · Claude Sonnet 5 via Emergent LLM key
                  {voice && voice.month && voice.month.drafts > 0 && (
                    <span data-testid="voice-metrics-inline">
                      {" · "}
                      <span className="font-mono text-indigo-700">{voice.month.acceptance_rate}%</span> accepted verbatim this month
                      {voice.month.avg_edit_distance > 0 && (
                        <span className="text-slate-400"> · avg edit {Math.round(voice.month.avg_edit_distance * 100)}%</span>
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] tracking-widest uppercase text-slate-400">Tone</span>
              {["warm", "brisk", "apologetic"].map((t) => (
                <button
                  key={t}
                  data-testid={`inbox-tone-${t}`}
                  onClick={() => setDraftTone(t)}
                  className={`px-3 h-8 rounded-full text-xs capitalize border transition ${
                    draftTone === t
                      ? "bg-indigo-700 text-white border-indigo-700"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-indigo-50"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          {KINDS.map((k) => (
            <button
              key={k.label}
              data-testid={`inbox-filter-${k.label.toLowerCase()}`}
              onClick={() => setKind(k.key)}
              className={`px-4 h-9 rounded-full text-xs tracking-wider uppercase border transition ${
                kind === k.key
                  ? "bg-teal-700 text-white border-teal-700"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-stone-50"
              }`}
            >
              {k.label}
            </button>
          ))}
          <div className="flex-1" />
          <button
            data-testid="inbox-voice-preview-btn"
            onClick={async () => {
              setVoiceOpen(true);
              if (voiceExamples) return;
              setVoiceBusy(true);
              try {
                const { data } = await api.get("/inbox/voice-examples", { params: { limit: 3 } });
                setVoiceExamples(data);
              } catch (err) {
                toast.error(apiError(err));
              } finally {
                setVoiceBusy(false);
              }
            }}
            className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-xs bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-50"
            title="See the last edits the AI is learning from"
          >
            <BookOpen className="w-3.5 h-3.5" />
            Voice preview
          </button>
        </div>

        <div className="ns-card overflow-hidden">
          {loading && <div className="text-center py-16 text-slate-400 italic">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-16 text-slate-400 italic">No messages yet.</div>
          )}
          {!loading && filtered.map((m) => {
            const canReply = (m.kind === "email" && m.to) || (m.kind === "sms" && m.to) || (m.kind === "ota" && m.from);
            const target = m.kind === "ota" ? m.from : m.to;
            const isOpen = replyOpenId === m.id;
            return (
              <div
                key={`${m.kind}-${m.id}`}
                data-testid={`inbox-item-${m.id}`}
                className="border-t border-slate-100 first:border-t-0"
              >
                <div className="p-5 flex items-start gap-4 hover:bg-teal-50/20">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                    !m.ok ? "bg-red-50 text-red-600" :
                    m.kind === "sms" ? "bg-amber-50 text-amber-700" :
                    m.kind === "ota" ? "bg-indigo-50 text-indigo-700" :
                    "bg-teal-50 text-teal-700"
                  }`}>
                    {m.kind === "sms" ? <MessageSquare className="w-4 h-4" /> :
                     m.kind === "ota" ? <Globe className="w-4 h-4" /> :
                     <Mail className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => m.contact_id && nav(`/contacts/${m.contact_id}`)}>
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-900 text-sm inline-flex items-center gap-2">
                          <User className="w-3.5 h-3.5 text-slate-400" />
                          {m.contact_name || target || "(unknown)"}
                          <DamageFlagBadge flag={damageFlags[m.contact_id]} testId={`inbox-damage-flag-${m.id}`} />
                          <span className="text-slate-400 font-normal text-xs">· {target}</span>
                        </div>
                        <div className="text-sm mt-1 truncate">{m.subject}</div>
                        {(m.kind === "sms" || m.kind === "ota") && m.preview && (
                          <div className="text-xs text-slate-500 mt-1 line-clamp-2">{m.preview}</div>
                        )}
                      </div>
                      <div className="shrink-0 flex flex-col items-end gap-1">
                        <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full ${
                          !m.ok ? "text-red-700 bg-red-50" :
                          m.kind === "ota" ? "text-indigo-700 bg-indigo-50" :
                          "text-slate-500 bg-slate-50"
                        }`}>
                          {(m.channel || m.kind || "email").replace(/_/g, " ")}
                        </span>
                        <span className="text-[11px] text-slate-400">{timeAgo(m.sent_at)}</span>
                      </div>
                    </div>
                    {(m.property_name || m.reference) && (
                      <div className="text-[11px] text-slate-400 mt-2 flex items-center gap-3">
                        {m.property_name && <span>{m.property_name}</span>}
                        {m.reference && <span>ref {String(m.reference).slice(0, 14)}…</span>}
                      </div>
                    )}
                  </div>
                  {canReply && !isOpen && (
                    <div className="shrink-0 flex flex-col gap-1.5 items-end">
                      <button
                        data-testid={`inbox-reply-btn-${m.id}`}
                        onClick={(e) => { e.stopPropagation(); openReply(m); }}
                        className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-teal-50 hover:border-teal-200 hover:text-teal-700"
                        title="Reply to this thread"
                      >
                        <Reply className="w-3 h-3 inline mr-1" /> Reply
                      </button>
                      <button
                        data-testid={`inbox-ai-draft-${m.id}`}
                        onClick={(e) => { e.stopPropagation(); draftReply(m); }}
                        disabled={draftingId === m.id}
                        className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full border border-indigo-200 text-indigo-700 bg-indigo-50/70 hover:bg-indigo-100 disabled:opacity-50"
                        title="Draft a reply with AI"
                      >
                        <Sparkles className={`w-3 h-3 inline mr-1 ${draftingId === m.id ? "animate-spin" : ""}`} /> {draftingId === m.id ? "Drafting…" : "AI draft"}
                      </button>
                    </div>
                  )}
                </div>
                {isOpen && (
                  <div data-testid={`inbox-reply-composer-${m.id}`} className="mx-5 mb-5 p-4 border border-teal-100 bg-teal-50/30 rounded-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="ns-label text-teal-700 inline-flex items-center gap-2">
                        <span>Reply to {target} {m.kind === "ota" && m.channel !== "whatsapp" ? `(via ${m.channel})` : ""}{m.channel === "whatsapp" ? " (via WhatsApp)" : ""}</span>
                      </div>
                      {m.channel === "whatsapp" && waTemplates.length > 0 && (
                        <div
                          data-testid={`inbox-whatsapp-templates-${m.id}`}
                          className="flex flex-wrap gap-1.5 items-center mr-2"
                        >
                          {waPromoteSuggestion && (
                            <button
                              type="button"
                              data-testid="inbox-whatsapp-promote-nudge"
                              title={waPromoteSuggestion.reason}
                              onClick={() => {
                                api.post(`/inbox/whatsapp-templates/${waPromoteSuggestion.template_id}/promote`)
                                  .then((r) => {
                                    setWaTemplates(r.data.templates || []);
                                    setWaPromoteSuggestion(null);
                                    toast.success(`Promoted "${waPromoteSuggestion.template_name}" to top`);
                                    // Session Z45/Z46 — confetti burst honours the "mute celebrations" workspace toggle.
                                    if (!confettiMuted) {
                                      try {
                                        if (confettiVariant === "rain") {
                                          const dur = 1200;
                                          const end = Date.now() + dur;
                                          const tick = () => {
                                            confetti({ particleCount: 4, startVelocity: 10, spread: 220, origin: { x: Math.random(), y: 0 }, scalar: 0.5, colors: ["#38bdf8","#a78bfa","#f472b6"] });
                                            if (Date.now() < end) requestAnimationFrame(tick);
                                          };
                                          tick();
                                        } else if (confettiVariant === "sparkle") {
                                          confetti({ particleCount: 60, spread: 110, startVelocity: 18, ticks: 60, scalar: 0.4, shapes: ["star"], colors: ["#fde047","#fbbf24","#fde68a"], origin: { y: 0.5 } });
                                        } else {
                                          confetti({ particleCount: 32, spread: 55, startVelocity: 28, scalar: 0.6, colors: ["#f59e0b", "#10b981", "#0d7a75"], origin: { y: 0.4 } });
                                        }
                                      } catch { /* confetti is best-effort */ }
                                    }
                                  })
                                  .catch(() => toast.error("Promote failed"));
                              }}
                              className="text-[9px] rounded-full bg-amber-100 text-amber-900 border border-amber-300 px-2 py-0.5 hover:bg-amber-200 transition"
                            >
                              ↑ Promote {waPromoteSuggestion.template_name}
                            </button>
                          )}
                          {waTemplates.map((t) => (
                            <button
                              key={t.id}
                              type="button"
                              data-testid={`inbox-whatsapp-template-${t.id}`}
                              onClick={() => {
                                setReplyBody(t.body);
                                // Session Z42 — fire-and-forget usage bump so
                                // owners can see which reply saves them time.
                                api.post(`/inbox/whatsapp-templates/${t.id}/used`)
                                  .then((r) => {
                                    setWaTemplates((prev) => prev.map((p) =>
                                      p.id === t.id ? { ...p, usage_count: r.data.usage_count } : p,
                                    ));
                                  })
                                  .catch(() => {});
                              }}
                              className="text-[10px] rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-1 hover:bg-emerald-100 transition inline-flex items-center gap-1"
                              title={t.body}
                            >
                              {t.name}
                              {t.usage_count > 0 && (
                                <span
                                  data-testid={`inbox-whatsapp-template-count-${t.id}`}
                                  className="text-[9px] text-emerald-500 font-medium"
                                >
                                  {t.usage_count}×
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="ml-auto flex items-center gap-2">
                        {(detectedCategory || effectiveCategory) && draftingId === m.id && (
                          <div className="relative inline-flex">
                            <button
                              type="button"
                              data-testid={`inbox-detected-category-${m.id}`}
                              onClick={() => setCategoryMenuFor(categoryMenuFor === m.id ? null : m.id)}
                              className={
                                "inline-flex items-center gap-1 px-2 h-5 rounded-full text-[10px] normal-case tracking-normal border transition " +
                                (overrideUsed
                                  ? "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
                                  : (detectedConfidence || 0) < 60
                                    ? "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                                    : "bg-indigo-50 text-indigo-700 border-indigo-100 hover:bg-indigo-100")
                              }
                              title={overrideUsed
                                ? "You forced this category — click to change"
                                : (detectedConfidence || 0) < 60
                                  ? "Low-confidence guess — sample narrowing was skipped. Click to override."
                                  : "The AI narrowed its learn-from-edits sample pool to this scenario. Click to override."}
                            >
                              <Sparkles className="w-2.5 h-2.5" />
                              {overrideUsed ? "forced" : "matched"}: {effectiveCategory || detectedCategory}
                              {!overrideUsed && typeof detectedConfidence === "number" && (
                                <span className="ml-0.5 opacity-70">· {detectedConfidence}%</span>
                              )}
                            </button>
                            {categoryMenuFor === m.id && (
                              <div
                                data-testid={`inbox-category-override-menu-${m.id}`}
                                className="absolute top-6 left-0 z-10 bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[140px]"
                                onMouseLeave={() => setCategoryMenuFor(null)}
                              >
                                {["arrival", "complaint", "upsell", "checkout"].map((c) => (
                                  <button
                                    key={c}
                                    type="button"
                                    data-testid={`inbox-category-override-${c}-${m.id}`}
                                    onClick={() => {
                                      setCategoryMenuFor(null);
                                      draftReply(m, c);
                                    }}
                                    className={"w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 " +
                                      (effectiveCategory === c ? "text-indigo-700 font-medium" : "text-slate-700")}
                                  >
                                    {c}
                                  </button>
                                ))}
                                <div className="border-t border-slate-100 my-1" />
                                <button
                                  type="button"
                                  data-testid={`inbox-category-override-auto-${m.id}`}
                                  onClick={() => {
                                    setCategoryMenuFor(null);
                                    draftReply(m);
                                  }}
                                  className="w-full text-left px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50"
                                >
                                  ↻ auto-detect
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <button onClick={() => setReplyOpenId(null)} className="text-slate-400 hover:text-slate-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {templates.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 items-center" data-testid="inbox-templates-strip">
                        <span className="text-[10px] tracking-widest uppercase text-slate-400 mr-1">Templates</span>
                        {[suggested, suggested2].filter(Boolean).map((s) => (
                          <span
                            key={`sug-${s.id}`}
                            data-testid={`inbox-template-suggested-${s.id}`}
                            className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-[11px] bg-indigo-100 text-indigo-800 border border-indigo-300"
                            title={`Best match (${Math.round((s.match_score || 0) * 100)}%) · ${s.body}`}
                          >
                            <button
                              onClick={() => applyTemplate(s)}
                              className="inline-flex items-center gap-1 hover:underline"
                              data-testid={`inbox-template-suggested-apply-${s.id}`}
                            >
                              <Sparkles className="w-2.5 h-2.5" />
                              {s.title}
                              <span className="text-[9px] text-indigo-600 ml-0.5">{Math.round((s.match_score || 0) * 100)}%</span>
                            </button>
                            <button
                              data-testid={`inbox-template-notmatch-${s.id}`}
                              onClick={() => flagNotAMatch(s)}
                              className="ml-1 w-4 h-4 rounded-full text-indigo-500 hover:text-red-600 hover:bg-white flex items-center justify-center"
                              title="Not a match — demote this suggestion next time"
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        ))}
                        {templates.filter((t) => t.id !== suggested?.id && t.id !== suggested2?.id).slice(0, 6).map((t) => (
                          <button
                            key={t.id}
                            data-testid={`inbox-template-${t.id}`}
                            onClick={() => applyTemplate(t)}
                            className="group inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-[11px] bg-white text-slate-700 hover:bg-teal-50 border border-slate-200"
                            title={`${t.body}${t.created_by_name ? " · by " + t.created_by_name : ""}`}
                          >
                            {t.title}
                            {t.usage_count > 0 && (
                              <span className="text-[9px] text-slate-400 ml-0.5">{t.usage_count}×</span>
                            )}
                            <span
                              onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id); }}
                              className="ml-1 w-4 h-4 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center"
                              role="button"
                              title="Delete template"
                            >
                              <X className="w-2.5 h-2.5" />
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    {m.kind !== "sms" && (
                      <Input
                        data-testid={`inbox-reply-subject-${m.id}`}
                        placeholder="Subject"
                        className="h-10 bg-white"
                        value={replySubject}
                        onChange={(e) => setReplySubject(e.target.value)}
                      />
                    )}
                    <Textarea
                      data-testid={`inbox-reply-body-${m.id}`}
                      rows={m.kind === "sms" ? 3 : 5}
                      placeholder={m.kind === "sms" ? "Type a quick SMS reply…" : "Type your reply…"}
                      className="bg-white"
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                    />
                    {m.kind === "ota" && (
                      <div className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                        This reply is sent as an <strong>email</strong>. OTAs like Airbnb / Booking.com relay it into their thread when the guest replies to the email notification.
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <button
                        data-testid={`inbox-save-template-${m.id}`}
                        onClick={saveTemplate}
                        disabled={!replyBody.trim()}
                        className="text-[11px] tracking-widest uppercase text-slate-500 hover:text-teal-700 disabled:opacity-40"
                        title="Save this reply as a reusable template"
                      >
                        Save as template →
                      </button>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" onClick={() => setReplyOpenId(null)}>Cancel</Button>
                        <Button
                          data-testid={`inbox-reply-draft-${m.id}`}
                          variant="outline"
                          onClick={() => draftReply(m)}
                          disabled={draftingId === m.id}
                          className="rounded-full px-5 border-indigo-200 text-indigo-700"
                        >
                          <Sparkles className={`w-3.5 h-3.5 mr-2 ${draftingId === m.id ? "animate-spin" : ""}`} /> {draftingId === m.id ? "Drafting…" : "AI draft"}
                        </Button>
                        <Button
                          data-testid={`inbox-reply-send-${m.id}`}
                          onClick={() => sendReply(m)}
                          disabled={replyBusy || !replyBody.trim()}
                          className="rounded-full ns-btn-primary px-6"
                        >
                          <Send className="w-3.5 h-3.5 mr-2" /> {replyBusy ? "Sending…" : "Send"}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={voiceOpen} onOpenChange={setVoiceOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="inbox-voice-preview-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-700" /> Voice preview
            </DialogTitle>
            <DialogDescription>
              The last {voiceExamples?.examples?.length ?? 0} edits you made before sending —
              this is exactly what the AI is mirroring when it drafts your next reply.
              {voiceExamples?.total ? ` Total edits on file: ${voiceExamples.total}.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-1.5 flex-wrap pb-2" data-testid="voice-category-filter">
            <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mr-1">Filter</span>
            {[
              { key: null, label: "All" },
              { key: "arrival", label: "Arrival" },
              { key: "complaint", label: "Complaint" },
              { key: "upsell", label: "Upsell" },
              { key: "checkout", label: "Checkout" },
            ].map((f) => (
              <button
                key={f.label}
                type="button"
                data-testid={`voice-category-filter-${(f.key || "all").toLowerCase()}`}
                onClick={async () => {
                  setVoiceCategoryFilter(f.key);
                  setVoiceBusy(true);
                  try {
                    const params = { limit: 10 };
                    if (f.key) params.category = f.key;
                    const { data } = await api.get("/inbox/voice-examples", { params });
                    setVoiceExamples(data);
                  } catch (err) {
                    toast.error(apiError(err));
                  } finally {
                    setVoiceBusy(false);
                  }
                }}
                className={"inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] border transition " +
                  (voiceCategoryFilter === f.key
                    ? "bg-indigo-700 text-white border-indigo-700"
                    : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-700")}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="overflow-y-auto flex-1 -mx-6 px-6 space-y-4">
            {voiceBusy && <div className="text-center py-16 text-slate-400 italic">Loading recent edits…</div>}
            {!voiceBusy && (!voiceExamples || (voiceExamples.examples || []).length === 0) && (
              <div className="text-center py-16 text-slate-400 italic" data-testid="voice-preview-empty">
                {voiceCategoryFilter ? (
                  <>
                    No {voiceCategoryFilter} samples yet — nail an edit on a{" "}
                    <span className="text-indigo-600 not-italic">{voiceCategoryFilter}</span>{" "}
                    reply to seed this pool.
                  </>
                ) : (
                  "No accepted edits yet — as soon as you tweak an AI draft before sending, it'll show up here."
                )}
              </div>
            )}
            {!voiceBusy && (voiceExamples?.examples || []).map((ex, i) => {
              const pinned = (Number(ex.weight) || 1) >= 2;
              return (
              <div key={ex.id || i} className={"rounded-xl border overflow-hidden " + (pinned ? "border-amber-300 bg-amber-50/40" : "border-slate-100 bg-white")} data-testid={`voice-example-${i}`}>
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between text-[10px] uppercase tracking-[0.16em] text-slate-500">
                  <span className="inline-flex items-center gap-2">
                    {pinned && <Star className="w-3 h-3 text-amber-500 fill-amber-400" data-testid={`voice-example-nailed-badge-${i}`} />}
                    {ex.kind || "email"} · tone {ex.tone || "warm"}
                    {ex.category && (
                      <span
                        data-testid={`voice-example-category-${i}`}
                        className="ml-1 inline-flex items-center px-1.5 h-4 rounded-full text-[9px] normal-case tracking-normal bg-indigo-50 text-indigo-700 border border-indigo-100"
                      >
                        {ex.category}
                      </span>
                    )}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    {ex.created_at ? new Date(ex.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
                    <button
                      type="button"
                      data-testid={`voice-example-nail-btn-${i}`}
                      onClick={async () => {
                        try {
                          if (pinned) {
                            await api.delete(`/inbox/draft-edits/${ex.id}/nail`);
                            toast.success("Un-pinned");
                          } else {
                            await api.post(`/inbox/draft-edits/${ex.id}/nail`);
                            toast.success("Pinned — the AI will lean on this next time");
                          }
                          // Refresh examples
                          const { data } = await api.get("/inbox/voice-examples", { params: { limit: 3 } });
                          setVoiceExamples(data);
                        } catch (err) {
                          toast.error(apiError(err));
                        }
                      }}
                      className={"inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] normal-case tracking-normal border transition " +
                        (pinned
                          ? "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200"
                          : "bg-white text-slate-500 border-slate-200 hover:border-amber-300 hover:text-amber-700")}
                    >
                      <Star className={"w-3 h-3 " + (pinned ? "fill-amber-500" : "")} />
                      {pinned ? "Nailed it" : "This nailed it"}
                    </button>
                  </span>
                </div>
                <div className="grid md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-rose-600 mb-1">AI wrote</div>
                    <div className="text-sm whitespace-pre-wrap text-slate-700 leading-6 font-serif" data-testid={`voice-example-ai-${i}`}>
                      {ex.ai_draft || <span className="italic text-slate-400">(empty)</span>}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-700 mb-1">You sent</div>
                    <div className="text-sm whitespace-pre-wrap text-slate-900 leading-6 font-serif" data-testid={`voice-example-final-${i}`}>
                      {ex.edited_final || <span className="italic text-slate-400">(empty)</span>}
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
          <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
            <Button
              data-testid="voice-preview-import-btn"
              variant="outline"
              className="rounded-full h-10 px-5 gap-2"
              onClick={() => document.getElementById("voice-import-file-input")?.click()}
            >
              <Sparkles className="w-4 h-4" />
              Import samples
            </Button>
            <input
              id="voice-import-file-input"
              data-testid="voice-import-file-input"
              type="file"
              accept="application/json,.json"
              style={{ display: "none" }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                try {
                  const text = await file.text();
                  const parsed = JSON.parse(text);
                  const examples = Array.isArray(parsed) ? parsed : parsed.examples;
                  if (!Array.isArray(examples) || !examples.length) {
                    toast.error("File must contain an examples array");
                    return;
                  }
                  const { data } = await api.post("/inbox/voice-examples/import", { examples });
                  toast.success(`Imported ${data.inserted} new, ${data.updated} updated · ${data.pinned} pinned`);
                  const refresh = await api.get("/inbox/voice-examples", { params: { limit: 3 } });
                  setVoiceExamples(refresh.data);
                } catch (err) {
                  toast.error(apiError(err) || "Import failed — check the JSON structure");
                }
              }}
            />
            <Button
              data-testid="voice-preview-download-btn"
              variant="outline"
              className="rounded-full h-10 px-5 gap-2"
              disabled={!voiceExamples?.examples?.length}
              onClick={() => {
                const examples = voiceExamples?.examples || [];
                if (!examples.length) return;
                const payload = {
                  exported_at: new Date().toISOString(),
                  total: voiceExamples.total,
                  examples: examples.map((e) => ({
                    id: e.id,
                    kind: e.kind || "email",
                    tone: e.tone || "warm",
                    category: e.category || null,
                    ai_draft: e.ai_draft,
                    edited_final: e.edited_final,
                    created_at: e.created_at,
                    weight: e.weight || 1,
                    nailed: (Number(e.weight) || 1) >= 2,
                  })),
                };
                const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const date = new Date().toISOString().slice(0, 10);
                const a = document.createElement("a");
                a.href = url;
                a.download = `newport-stays-voice-examples-${date}.json`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success("Voice sample pack saved");
              }}
            >
              <Download className="w-4 h-4" />
              Download samples
            </Button>
            <Button
              variant="outline"
              className="rounded-full h-10 px-5"
              onClick={() => setVoiceOpen(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
